import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Mail, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import Logo from "../components/Logo";

/**
 * Iter-32 — Démarre un flow OAuth Google DIRECT (sans intermédiaire Emergent).
 * Le backend renvoie l'URL d'authent Google → redirection navigateur.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
async function startGoogleLogin() {
    const redirectUri = `${window.location.origin}/auth/google`;
    try {
        const { data } = await api.get(
            `/auth/google/start?redirect_uri=${encodeURIComponent(redirectUri)}`,
        );
        window.location.href = data.auth_url;
    } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(
            typeof detail === "string"
                ? detail
                : "Impossible de démarrer la connexion Google. Réessaie.",
        );
    }
}

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // If the user got kicked out by the global 401 interceptor (e.g. account disabled by admin),
    // explain it once and clean the URL so a refresh doesn't keep showing the toast.
    useEffect(() => {
        if (searchParams.get("disabled") === "1") {
            toast.error("Ton compte a été désactivé par un administrateur. Contacte-le pour plus d'infos.", { duration: 6000 });
            const next = new URLSearchParams(searchParams);
            next.delete("disabled");
            setSearchParams(next, { replace: true });
        }
        // iter-22: show the reason for a failed OAuth attempt (e.g. invitation required)
        const err = searchParams.get("error");
        if (err && err !== "oauth") {
            toast.error(err, { duration: 6000 });
            const next = new URLSearchParams(searchParams);
            next.delete("error");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = await login(email, password);
            toast.success("Bienvenue dans Shariti Academy");
            // Iter-27.100 — Onboarding obligatoire pour learners ET managers
            // (mêmes messages de bienvenue, mêmes valeurs Shariti, jusqu'à
            // atterrir sur leur premier module respectif).
            if (
                (data?.role === "learner" || data?.role === "manager") &&
                data?.onboarding_completed === false
            ) {
                navigate("/onboarding");
            } else if (data?.role === "association") {
                // Iter-27.64 — Comptes asso : direct vers leur backoffice partenaire
                navigate("/asso-admin");
            } else {
                navigate("/dashboard");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col lg:flex-row bg-shariti-deep-black overflow-hidden">
            {/* Left visual */}
            <div className="hidden lg:flex relative w-1/2 items-center justify-center overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-shariti-orange/30 blur-[120px] animate-blob" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-shariti-yellow/15 blur-[100px] animate-blob" />
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="relative z-10 max-w-md p-10"
                >
                    <Logo
                        variant="full"
                        showAcademy
                        academySize="lg"
                        className="h-32 w-auto drop-shadow-[0_0_40px_rgba(238,114,46,0.45)]"
                        wrapperClassName="mb-8 items-start"
                    />
                    <h1 className="text-6xl font-extrabold text-white tracking-tight leading-none mb-6">
                        Devenez<br />
                        <span className="text-shariti-orange">imbattable</span><br />
                        sur le terrain.
                    </h1>
                    <p className="text-white/60 font-light text-lg mb-10 text-balance">
                        La plateforme de formation premium pour propulser la performance des recruteurs de donateurs.
                    </p>
                    <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div
                                key={i}
                                className="h-1 flex-1 rounded-full bg-shariti-orange/30"
                                style={{
                                    background: i <= 3 ? "#EE722E" : "rgba(238,114,46,0.2)",
                                }}
                            />
                        ))}
                    </div>
                </motion.div>
            </div>

            {/* Right form */}
            <div className="flex-1 flex items-center justify-center px-6 py-10 relative">
                <div className="lg:hidden absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full bg-shariti-orange/25 blur-[100px]" />
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md relative z-10"
                >
                    <div className="lg:hidden mb-10 flex justify-center">
                        <Logo
                            variant="full"
                            showAcademy
                            academySize="md"
                            className="h-20 w-auto drop-shadow-[0_0_24px_rgba(238,114,46,0.35)]"
                        />
                    </div>

                    <p className="text-xs font-thin tracking-[0.3em] text-shariti-orange-light uppercase mb-2">
                        Bon retour
                    </p>
                    <h2 className="text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-2">
                        Connexion
                    </h2>
                    <p className="text-white/50 font-light mb-8">
                        Reprends là où tu t'es arrêté·e.
                    </p>

                    <button
                        type="button"
                        onClick={startGoogleLogin}
                        data-testid="google-login-button"
                        className="w-full flex items-center justify-center gap-3 glass-card rounded-2xl py-3.5 mb-5 hover:border-shariti-orange/40 transition-all font-bold text-white"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" opacity="0.9"/>
                            <path fill="#EE722E" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity="0.7"/>
                            <path fill="#FCDB53" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continuer avec Google
                    </button>

                    <div className="flex items-center gap-3 mb-5">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-xs font-thin tracking-widest text-white/40 uppercase">
                            ou avec ton email
                        </span>
                        <div className="flex-1 h-px bg-white/10" />
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            <input
                                type="email"
                                placeholder="ton@email.fr"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email-input"
                                className="w-full pl-12 pr-4 py-3.5 glass-card rounded-2xl text-white placeholder-white/30 focus:outline-none focus:border-shariti-orange/60 transition-all"
                            />
                        </div>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            <input
                                type={showPwd ? "text" : "password"}
                                placeholder="mot de passe"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password-input"
                                className="w-full pl-12 pr-12 py-3.5 glass-card rounded-2xl text-white placeholder-white/30 focus:outline-none focus:border-shariti-orange/60 transition-all"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-shariti-orange/15 text-shariti-orange hover:bg-shariti-orange/25 transition-colors"
                                data-testid="login-password-toggle"
                                aria-label={
                                    showPwd
                                        ? "Masquer le mot de passe"
                                        : "Afficher le mot de passe"
                                }
                            >
                                {showPwd ? (
                                    <EyeOff className="w-4 h-4" />
                                ) : (
                                    <Eye className="w-4 h-4" />
                                )}
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-button"
                            className="w-full flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3.5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                        >
                            {loading ? "Connexion..." : "Se connecter"}
                            {!loading && <ArrowRight className="w-5 h-5" />}
                        </button>
                    </form>

                    <div className="mt-4 flex justify-center">
                        <Link
                            to="/forgot-password"
                            data-testid="login-forgot-password-link"
                            className="text-sm text-white/55 hover:text-shariti-orange-light transition-colors underline-offset-4 hover:underline"
                        >
                            Mot de passe oublié ?
                        </Link>
                    </div>

                    <p className="text-center text-sm text-white/50 mt-6">
                        Pas encore de compte ?{" "}
                        <Link
                            to="/register"
                            data-testid="login-register-link"
                            className="text-shariti-orange font-bold"
                        >
                            Créer mon compte
                        </Link>
                    </p>
                </motion.div>
            </div>
        </div>
    );
}
